import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { IconType } from 'react-icons';
import { FiBell, FiCheck, FiX, FiCheckCircle } from 'react-icons/fi';
import { RiNotificationLine, RiNotificationFill } from 'react-icons/ri';
import { useHistory } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { formatTimeAgo } from '../../services/utilitarios';

export type NotificationPriority = 'high' | 'medium' | 'low';

export interface Notification {
  id: string | number;
  title: string;
  description: string;
  date?: Date | string;
  priority: NotificationPriority;
  read: boolean;
  icon?: IconType;
  action?: () => void;
  details?: string;
  url?: string;
}

interface NotificationBellProps {
  notifications?: Notification[];
  onNotificationClick?: (notification: Notification, index: number) => void;
  onClearAll?: () => void;
  onViewAll?: () => void;
  showAllText?: string;
  emptyText?: string;
  badgeColor?: string;
  bellIcon?: React.ReactNode;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
  viewAllUrl?: string;
  resetNotifications?: boolean;
}

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

const fadeSlide = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const NotificationContainer = styled.div`
  position: relative;
  z-index: 1000;
  display: inline-block;
`;

const BellButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #444;
  font-size: 1.3rem;
  position: relative;
  transition: background 0.25s ease, transform 0.25s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
    transform: scale(1.05);
  }

  &.has-unread {
    animation: ${pulse} 1.5s infinite;
  }
`;

const Badge = styled.span<{ color: string }>`
  position: absolute;
  top: 2px;
  right: 2px;
  background: ${props => props.color};
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
`;

const Popover = styled.div`
  position: fixed;
  top: 60px;
  right: 20px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.12);
  width: 360px;
  max-height: 500px;
  display: flex;
  flex-direction: column;
  z-index: 99999;
  animation: ${fadeSlide} 0.25s ease forwards;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const CountBadge = styled.span`
  background: #f0f0f0;
  color: #666;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
  margin-left: 8px;
  font-weight: normal;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #888;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #555;
  }
`;

const NotificationsList = styled.div`
  overflow-y: auto;
  flex-grow: 1;
`;

const NotificationItem = styled.div<{ priority: NotificationPriority; read: boolean }>`
  padding: 12px 18px;
  cursor: pointer;
  border-left: 3px solid ${props =>
    props.priority === 'high' ? '#ff4d4f' :
      props.priority === 'medium' ? '#faad14' : '#52c41a'};
  background: ${props => !props.read ? '#fdfdfd' : '#fff'};
  display: flex;
  gap: 12px;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    background: #f7f9fc;
    box-shadow: inset 0 0 0 1px rgba(0,0,0,0.05);
  }
`;

const NotificationIcon = styled.div<{ priority: NotificationPriority }>`
  color: ${props =>
    props.priority === 'high' ? '#ff4d4f' :
      props.priority === 'medium' ? '#faad14' : '#52c41a'};
  font-size: 18px;
  display: flex;
  align-items: center;
`;

const NotificationContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const NotificationTitle = styled.h4<{ $read: boolean }>`
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: ${props => props.$read ? '400' : '600'};
  color: ${props => props.$read ? '#555' : '#222'};
`;

const NotificationBody = styled.div`
  margin-top: 4px;
`;

const NotificationDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 13px;
  line-height: 1.4;
`;

const NotificationTime = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #000;
`;

const EmptyState = styled.div`
  padding: 30px;
  text-align: center;
  color: #666;
  font-size: 14px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const EmptyIcon = styled.div`
  color: #52c41a;
  font-size: 32px;
  opacity: 0.8;
`;

const EmptyMessage = styled.p`
  margin: 0;
  font-weight: 500;
  color: #444;
`;

const Footer = styled.div`
  padding: 12px;
  text-align: center;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

const ViewAllButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #1890ff;
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(24, 144, 255, 0.08);
  }
`;

const LoadingContainer = styled.div`
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #1890ff;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const NotificationBell: React.FC<NotificationBellProps> = ({
  notifications = [],
  onNotificationClick,
  onClearAll,
  onViewAll,
  showAllText = "Ver todas",
  emptyText = "Tudo resolvido",
  badgeColor = "#ff4d4f",
  bellIcon = <RiNotificationLine size={20} />,
  isOpen = false,
  onToggle,
  viewAllUrl = '/notifications',
  resetNotifications = false
}) => {
  const [internalOpen, setInternalOpen] = useState(isOpen);
  const [markedAsRead, setMarkedAsRead] = useState<string[]>([]);
  const [clearedNotifications, setClearedNotifications] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useHistory();
  const popoverRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  // Load data from localStorage only once on mount
  useEffect(() => {
    const storedCleared = localStorage.getItem('clearedNotifications');
    const storedRead = localStorage.getItem('markedAsRead');
    
    if (storedCleared) setClearedNotifications(JSON.parse(storedCleared));
    if (storedRead) setMarkedAsRead(JSON.parse(storedRead));
  }, []);

  // Reset notifications when needed
  useEffect(() => {
    if (resetNotifications) {
      localStorage.removeItem('clearedNotifications');
      localStorage.removeItem('markedAsRead');
      setClearedNotifications([]);
      setMarkedAsRead([]);
    }
  }, [resetNotifications]);

  // Sync with controlled isOpen prop
  useEffect(() => {
    if (isOpen !== undefined && onToggle) {
      setInternalOpen(isOpen);
    }
  }, [isOpen, onToggle]);

  // Calculate counts and filtered notifications using useMemo for performance
  const { unreadCount, totalCount, filteredNotifications } = useMemo(() => {
    const filtered = notifications.filter(n => !clearedNotifications.includes(n.id.toString()));
    
    const unread = filtered.filter(n => 
      !markedAsRead.includes(n.id.toString()) && !n.read
    ).length;

    return {
      unreadCount: unread,
      totalCount: filtered.length,
      filteredNotifications: filtered.map(n => ({
        ...n,
        read: markedAsRead.includes(n.id.toString()) || n.read
      }))
    };
  }, [notifications, clearedNotifications, markedAsRead]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isOpenState = onToggle ? isOpen : internalOpen;

      if (isOpenState &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(event.target as Node)) {
        handleToggle(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [internalOpen, isOpen, onToggle]);

  // Memoized notification click handler
  const handleNotificationClick = useCallback((notification: Notification, index: number) => {
    if (!markedAsRead.includes(notification.id.toString())) {
      const newMarkedAsRead = [...markedAsRead, notification.id.toString()];
      setMarkedAsRead(newMarkedAsRead);
      localStorage.setItem('markedAsRead', JSON.stringify(newMarkedAsRead));
    }

    onNotificationClick?.(notification, index);
    handleToggle(false);
  }, [markedAsRead, onNotificationClick]);

  // Memoized clear all handler
  const handleClearAll = useCallback(() => {
    const allIds = notifications.map(n => n.id.toString());
    const newClearedNotifications = [...clearedNotifications, ...allIds];
    const newMarkedAsRead = [...markedAsRead, ...allIds];
    
    setClearedNotifications(newClearedNotifications);
    setMarkedAsRead(newMarkedAsRead);
    localStorage.setItem('clearedNotifications', JSON.stringify(newClearedNotifications));
    localStorage.setItem('markedAsRead', JSON.stringify(newMarkedAsRead));

    onClearAll?.();
  }, [notifications, clearedNotifications, markedAsRead, onClearAll]);

  // Memoized view all handler
  const handleViewAll = useCallback(() => {
    const allIds = notifications.map(n => n.id.toString());
    const newMarkedAsRead = [...markedAsRead, ...allIds];
    
    setMarkedAsRead(newMarkedAsRead);
    localStorage.setItem('markedAsRead', JSON.stringify(newMarkedAsRead));

    handleToggle(false);
    if (viewAllUrl) {
      navigate.push(viewAllUrl);
    } else {
      onViewAll?.();
    }
  }, [notifications, markedAsRead, viewAllUrl, navigate, onViewAll]);

  // Optimized toggle function with loading state
  const handleToggle = useCallback(async (open: boolean) => {
    if (open) {
      setIsLoading(true);
      
      // Small delay to show loading state and prevent UI freeze
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Mark all current notifications as read when opening
      const currentIds = notifications.map(n => n.id.toString());
      const newMarkedAsRead = [...new Set([...markedAsRead, ...currentIds])];
      setMarkedAsRead(newMarkedAsRead);
      localStorage.setItem('markedAsRead', JSON.stringify(newMarkedAsRead));
      
      setIsLoading(false);
    }

    if (onToggle) onToggle(open);
    else setInternalOpen(open);
  }, [notifications, markedAsRead, onToggle]);

  // Sort notifications by priority and date
  const sortedNotifications = useMemo(() => {
    return [...filteredNotifications].sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      
      // First sort by priority
      if (priorityOrder[b.priority] !== priorityOrder[a.priority]) {
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      
      // Then by date if priorities are equal
      if (a.date && b.date) {
        const aDate = typeof a.date === 'string' ? new Date(a.date) : a.date;
        const bDate = typeof b.date === 'string' ? new Date(b.date) : b.date;
        return bDate.getTime() - aDate.getTime();
      }
      
      return 0;
    });
  }, [filteredNotifications]);

  // Get default icon based on priority
  const getDefaultIcon = useCallback((priority: NotificationPriority) => {
    switch (priority) {
      case 'high': return <FiBell />;
      case 'medium': return <RiNotificationLine />;
      case 'low': return <FiCheck />;
      default: return <RiNotificationLine />;
    }
  }, []);

  return (
    <NotificationContainer>
      <BellButton
        ref={bellRef}
        onClick={() => handleToggle(!internalOpen)}
        className={unreadCount > 0 ? 'has-unread' : ''}
        aria-label="Notificações"
      >
        {unreadCount > 0 ? <RiNotificationFill size={20} /> : bellIcon}
        {unreadCount > 0 && (
          <Badge color={badgeColor}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </BellButton>

      {(onToggle ? isOpen : internalOpen) && (
        <Popover ref={popoverRef} role="dialog" aria-label="Painel de notificações">
          <Header>
            <Title>
              Notificações
              {totalCount > 0 && <CountBadge>{totalCount}</CountBadge>}
            </Title>
            {!isLoading && sortedNotifications.length > 0 && (
              <ClearButton onClick={handleClearAll} aria-label="Limpar todas as notificações">
                <FiX size={14} />
                Limpar todas
              </ClearButton>
            )}
          </Header>

          {isLoading ? (
            <LoadingContainer>
              <LoadingSpinner aria-label="Carregando notificações" />
            </LoadingContainer>
          ) : sortedNotifications.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <FiCheckCircle />
              </EmptyIcon>
              <EmptyMessage>{emptyText}</EmptyMessage>
              <span>Sem pendências no momento</span>
            </EmptyState>
          ) : (
            <>
              <NotificationsList>
                {sortedNotifications.map((notification, index) => (
                  <NotificationItem
                    key={notification.id}
                    priority={notification.priority}
                    read={notification.read}
                    onClick={() => handleNotificationClick(notification, index)}
                    aria-label={`Notificação: ${notification.title}`}
                  >
                    <NotificationIcon priority={notification.priority}>
                      {notification.icon ?
                        React.createElement(notification.icon) :
                        getDefaultIcon(notification.priority)}
                    </NotificationIcon>
                    <NotificationContent>
                      <NotificationHeader>
                        <NotificationTitle $read={notification.read}>
                          {notification.title}
                        </NotificationTitle>
                      </NotificationHeader>
                      <NotificationBody>
                        <NotificationDescription>
                          {notification.description}
                        </NotificationDescription>
                        {notification.date && (
                          <NotificationTime>
                            {formatTimeAgo(notification.date)}
                          </NotificationTime>
                        )}
                      </NotificationBody>
                    </NotificationContent>
                  </NotificationItem>
                ))}
              </NotificationsList>

              {sortedNotifications.length > 0 && (
                <Footer>
                  <ViewAllButton
                    onClick={handleViewAll}
                    data-testid="view-all-button"
                    aria-label="Ver todas as notificações"
                  >
                    {showAllText}
                  </ViewAllButton>
                </Footer>
              )}
            </>
          )}
        </Popover>
      )}
    </NotificationContainer>
  );
};

export default NotificationBell;